import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import {
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';

import { CompactTablePagination, EmptyState, ErrorState, LoadingState, TablePageShell } from '@/shared/ui';

import { useProductSets } from '../../hooks';
import { usePromoPermissions } from '../../lib/usePromoPermissions';
import type { ToastSeverity } from '../../lib/useToast';
import type { ProductSetResponse } from '../../model/types';
import { ProductSetEditorDrawer } from './ProductSetEditorDrawer';

export interface ProductSetsPanelProps {
  onResult: (severity: ToastSeverity, message: string) => void;
}

function describeComposition(set: ProductSetResponse, t: (key: string, opts?: Record<string, unknown>) => string): string {
  if (set.kind === 'STATIC') {
    const productCount = set.entries.filter((e) => e.memberKind === 'PRODUCT' && e.inclusion !== 'EXCLUDE').length;
    return t('productSets.compositionStatic', { count: productCount });
  }
  const parts: string[] = [];
  if (set.filterParams?.categoryIds?.length) parts.push(t('productSets.compositionCategories', { count: set.filterParams.categoryIds.length }));
  if (set.filterParams?.brandCodes?.length) parts.push(t('productSets.compositionBrands', { count: set.filterParams.brandCodes.length }));
  if (set.filterParams?.minPrice != null || set.filterParams?.maxPrice != null) parts.push(t('productSets.compositionPriceRange'));
  return parts.length > 0 ? parts.join(', ') : t('productSets.compositionEmptyFilter');
}

/**
 * "Product sets" — reusable product sets (plan-011 §8.10).
 *
 * Two contract-driven adaptations from the design spec:
 *  - No delete endpoint exists in the shipped BFF (only create/get/update/materialize) — the row
 *    menu offers Edit only; delete is omitted rather than wired to a non-existent call.
 *  - The spec's "used in N promotions" column needs each promotion's full condition/action tree
 *    (the list-summary DTO carries neither), which would mean an N+1 detail fetch per promotion
 *    just to render a table column — omitted rather than shown as a fake always-zero count.
 */
export function ProductSetsPanel({ onResult }: ProductSetsPanelProps) {
  const { t } = useTranslation('promotions');
  const { canManage } = usePromoPermissions();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [editing, setEditing] = useState<ProductSetResponse | undefined>(undefined);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [menuTarget, setMenuTarget] = useState<{ anchor: HTMLElement; set: ProductSetResponse } | null>(null);

  const query = useProductSets({ size: 200 });

  const filtered = useMemo(() => {
    const all = query.data?.content ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter((s) => s.name.toLowerCase().includes(q));
  }, [query.data, search]);

  const pageItems = filtered.slice(page * size, page * size + size);

  if (query.isLoading) return <LoadingState label={t('productSets.loading')} />;
  if (query.isError) {
    return (
      <ErrorState
        title={t('productSets.loadError.title')}
        message={query.error?.message ?? t('common:error.unknown')}
        onRetry={() => void query.refetch()}
      />
    );
  }

  if ((query.data?.content.length ?? 0) === 0) {
    return (
      <EmptyState
        icon={<Inventory2OutlinedIcon sx={{ fontSize: 48 }} />}
        title={t('productSets.empty.title')}
        description={t('productSets.empty.description')}
        action={
          canManage ? (
            <Button
              variant="contained"
              startIcon={<AddRoundedIcon />}
              onClick={() => {
                setEditing(undefined);
                setDrawerOpen(true);
              }}
            >
              {t('productSets.newSet')}
            </Button>
          ) : undefined
        }
      />
    );
  }

  return (
    <>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }} alignItems={{ xs: 'stretch', sm: 'center' }}>
        <TextField
          size="small"
          placeholder={t('productSets.searchPlaceholder')}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          sx={{ minWidth: 260 }}
        />
        <Stack sx={{ flex: 1 }} />
        {canManage ? (
          <Button
            variant="contained"
            startIcon={<AddRoundedIcon />}
            onClick={() => {
              setEditing(undefined);
              setDrawerOpen(true);
            }}
          >
            {t('productSets.newSet')}
          </Button>
        ) : null}
      </Stack>

      <TablePageShell
        footer={
          <CompactTablePagination
            count={filtered.length}
            page={page}
            rowsPerPage={size}
            onPageChange={(_e, next) => setPage(next)}
            onRowsPerPageChange={(e) => {
              setSize(parseInt(e.target.value, 10));
              setPage(0);
            }}
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} ${t('common:pagination.of')} ${count}`}
          />
        }
      >
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('productSets.table.name')}</TableCell>
                <TableCell>{t('productSets.table.kind')}</TableCell>
                <TableCell>{t('productSets.table.composition')}</TableCell>
                <TableCell align="right">{t('promotionsList.table.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pageItems.map((set) => {
                return (
                  <TableRow key={set.id} hover>
                    <TableCell>{set.name}</TableCell>
                    <TableCell>
                      <Chip size="small" variant="outlined" label={t(`productSetEditorDrawer.kind${set.kind === 'STATIC' ? 'Static' : 'Filter'}`)} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {describeComposition(set, t)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        aria-label={t('promotionsList.rowMenuAria')}
                        onClick={(e) => setMenuTarget({ anchor: e.currentTarget, set })}
                      >
                        <MoreVertRoundedIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </TablePageShell>

      <Menu anchorEl={menuTarget?.anchor} open={Boolean(menuTarget)} onClose={() => setMenuTarget(null)}>
        <MenuItem
          disabled={!canManage}
          onClick={() => {
            if (menuTarget) {
              setEditing(menuTarget.set);
              setDrawerOpen(true);
            }
            setMenuTarget(null);
          }}
        >
          {t('common:action.edit')}
        </MenuItem>
        <Tooltip title={t('productSets.noDeleteHint')} placement="right">
          <span>
            <MenuItem disabled>{t('common:action.delete')}</MenuItem>
          </span>
        </Tooltip>
      </Menu>

      <ProductSetEditorDrawer
        open={drawerOpen}
        editing={editing}
        onClose={() => setDrawerOpen(false)}
        onSaved={() => void query.refetch()}
        onResult={onResult}
      />
    </>
  );
}
